import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import Sidebar from '../common/sidebar';
import Footer from '../common/footer';

const DEFAULT_FORM = {
  legal_name: '',
  sender_email: '',
  sender_phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
  tax_id: '',
  vat_number: '',
  invoice_prefix: 'INV',
  next_invoice_number: '1',
  default_currency: 'EUR',
  default_payment_terms_days: '14',
  default_tax_rate: '0.00',
  payment_instructions: '',
  footer_notes: '',
};

export default function InvoiceSettingsPage() {
  useTranslation();
  const auth = useSelector((state) => state.auth);
  const history = useNavigate();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const requestConfig = useMemo(() => ({
    headers: {
      Authorization: `Bearer ${auth.token}`,
      'Content-Type': 'application/json',
    },
  }), [auth.token]);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/invoice/settings?workspace_id=${auth.workspace_id}`, requestConfig);
      setForm({
        ...DEFAULT_FORM,
        ...(res.data.settings || {}),
        next_invoice_number: String(res.data.settings?.next_invoice_number || 1),
        default_payment_terms_days: String(res.data.settings?.default_payment_terms_days || 14),
        default_tax_rate: String(res.data.settings?.default_tax_rate || '0.00'),
      });
    } catch (err) {
      setError('Failed to load invoice settings.');
    } finally {
      setLoading(false);
    }
  }, [auth.workspace_id, requestConfig]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put('/api/invoice/settings', JSON.stringify({
        ...form,
        workspace_id: auth.workspace_id,
      }), requestConfig);
      history('/invoices');
    } catch (err) {
      setError('Failed to save invoice settings.');
    } finally {
      setSaving(false);
    }
  };

  const renderInput = (key, label, options = {}) => (
    <div className={`pm-field${options.wide ? ' pm-field--wide' : ''}`}>
      <label className='pm-label'>{label}</label>
      {options.type === 'textarea' ? (
        <textarea
          className='pm-input pm-input--textarea'
          rows={options.rows || 4}
          value={form[key]}
          onChange={(e) => updateField(key, e.target.value)}
          placeholder={options.placeholder || ''}
        />
      ) : (
        <input
          className='pm-input'
          type={options.type || 'text'}
          value={form[key]}
          onChange={(e) => updateField(key, e.target.value)}
          placeholder={options.placeholder || ''}
        />
      )}
    </div>
  );

  return (
    <div>
      <Sidebar />
      <div className='content-wrap' id='wrapper'>
        <div className='main'>
          <div className='cp-shell'>
            <div className='cp-topbar'>
              <button type='button' className='cp-back' onClick={() => history('/invoices')}>{tr("Back to Invoices")}</button>
              <div className='cp-actions'>
                <button type='button' className='cp-btn cp-btn--secondary' onClick={() => history('/invoices')}>{tr("Cancel")}</button>
                <button type='button' className='cp-btn cp-btn--primary' onClick={handleSave} disabled={saving || loading}>
                  {saving ? tr("Saving...") : tr("Save settings")}
                </button>
              </div>
            </div>

            <div className='cp-hero'>
              <div>
                <div className='cp-eyebrow'>{tr("Invoice Settings")}</div>
                <h1>{tr("Finance identity for your workspace")}</h1>
                <p>{tr("Define the legal sender profile, numbering system, and payment defaults every invoice should inherit.")}</p>
              </div>
              <div className='cp-summary-card'>
                <span className='pm-readiness-label'>{tr("Module scope")}</span>
                <strong className='pm-readiness-value'>{tr("Workspace-level defaults")}</strong>
                <div className='cp-summary-meta'>{tr("Used by every draft and issued invoice")}</div>
              </div>
            </div>

            {error && <div className='iv-error'>{error}</div>}

            {loading ? (
              <div className='cp-loading-card'>{tr("Loading invoice settings...")}</div>
            ) : (
              <div className='cp-grid'>
                <div className='cp-main'>
                  <div className='pm-section'>
                    <div className='pm-section-head'>
                      <h4>{tr("Sender Identity")}</h4>
                      <p>{tr("This is the legal and operational identity clients should see on every invoice.")}</p>
                    </div>
                    <div className='pm-grid pm-grid--3'>
                      {renderInput('legal_name', tr("Legal business name"), { placeholder: 'Minutezilla GmbH' })}
                      {renderInput('sender_email', tr("Finance email"), { type: 'email', placeholder: 'finance@minutezilla.com' })}
                      {renderInput('sender_phone', tr("Finance phone"), { placeholder: '+49 30 1234 5678' })}
                      {renderInput('tax_id', tr("Tax ID"), { get placeholder() { return tr("Company tax identifier"); } })}
                      {renderInput('vat_number', tr("VAT number"), { get placeholder() { return tr("EU VAT number if applicable"); } })}
                    </div>
                  </div>

                  <div className='pm-section'>
                    <div className='pm-section-head'>
                      <h4>{tr("Billing Address")}</h4>
                      <p>{tr("Used for invoice headers, payment compliance, and formal sender references.")}</p>
                    </div>
                    <div className='pm-grid pm-grid--2'>
                      {renderInput('address_line1', tr("Address line 1"))}
                      {renderInput('address_line2', tr("Address line 2"))}
                      {renderInput('city', tr("City"))}
                      {renderInput('state', tr("State / region"))}
                      {renderInput('postal_code', tr("Postal code"))}
                      {renderInput('country', tr("Country"))}
                    </div>
                  </div>

                  <div className='pm-section'>
                    <div className='pm-section-head'>
                      <h4>{tr("Invoice Defaults")}</h4>
                      <p>{tr("These defaults keep invoice generation consistent across freelancers, managers, and teams.")}</p>
                    </div>
                    <div className='pm-grid pm-grid--3'>
                      {renderInput('invoice_prefix', tr("Invoice prefix"))}
                      {renderInput('next_invoice_number', tr("Next invoice number"), { type: 'number' })}
                      {renderInput('default_currency', tr("Default currency"))}
                      {renderInput('default_payment_terms_days', tr("Payment terms (days)"), { type: 'number' })}
                      {renderInput('default_tax_rate', tr("Default tax rate %"), { type: 'number' })}
                    </div>
                  </div>

                  <div className='pm-section'>
                    <div className='pm-section-head'>
                      <h4>{tr("Payment Guidance")}</h4>
                      <p>{tr("Make remittance simple for clients and predictable for your finance operations.")}</p>
                    </div>
                    <div className='pm-grid pm-grid--2'>
                      {renderInput('payment_instructions', tr("Payment instructions"), { type: 'textarea', wide: true, rows: 4, get placeholder() { return tr("Bank details, IBAN, routing, remittance instructions."); } })}
                      {renderInput('footer_notes', tr("Footer notes"), { type: 'textarea', wide: true, rows: 4, get placeholder() { return tr("Optional legal footer or finance note."); } })}
                    </div>
                  </div>
                </div>

                <div className='cp-side'>
                  <div className='cp-side-card'>
                    <h3>{tr("What this controls")}</h3>
                    <ul className='cp-side-list'>
                      <li>{tr("Invoice numbering and finance identity")}</li>
                      <li>{tr("Default currency and payment terms")}</li>
                      <li>{tr("Sender details reused across drafts")}</li>
                      <li>{tr("Payment instructions for every invoice")}</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
